import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Store as StoreIcon, Search, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorStoresPage — `/platform/companies/:cid/stores`
 *
 * Lists every Store under a tenant. Each row navigates to the per-store
 * detail page. "Add store" dialog kept here (moved from the legacy inline
 * list on TenantDetailPage).
 *
 * Read-only except for the existing "Add store" write — which already
 * existed pre-Phase-2F via POST /platform/companies/{cid}/stores.
 */
export default function OperatorStoresPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addCode, setAddCode] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [det, st] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/stores`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setStores(st.data.stores || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return stores;
    return stores.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(term) ||
        (s.code || "").toLowerCase().includes(term) ||
        (s.id || "").toLowerCase().includes(term),
    );
  }, [stores, search]);

  const addStore = async () => {
    const cleaned = addName.trim();
    if (!cleaned) return toast.error("Store name is required");
    setAdding(true);
    try {
      await api.post(`/platform/companies/${cid}/stores`, {
        name: cleaned,
        code: addCode.trim() || null,
      });
      toast.success("Store added");
      setAddOpen(false);
      setAddName("");
      setAddCode("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setAdding(false);
    }
  };

  const GRID = "minmax(0,2fr) 1fr minmax(0,1fr) auto";

  return (
    <div data-testid="operator-stores-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-stores-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title="Stores"
        subtitle={`${stores.length} store${stores.length === 1 ? "" : "s"} in this tenant. Click a row to drill into its users, batches, removals, and product requests.`}
        actions={
          <Button
            tier="primary"
            onClick={() => setAddOpen(true)}
            data-testid="operator-stores-add-button"
          >
            <Plus size={14} /> Add store
          </Button>
        }
      />

      <Card variant="raised" className="p-3 mb-4">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by store name, code, or ID…"
            className="pl-9"
            data-testid="operator-stores-search"
          />
        </div>
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Store</div>
            <div>Code</div>
            <div>ID</div>
            <div className="text-right">Action</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<StoreIcon size={32} />}
              title={search ? "No stores match" : "No stores yet"}
              description={
                search
                  ? "Adjust the search term or clear it to see all stores."
                  : "Create the first store for this tenant."
              }
              data-testid="operator-stores-empty"
            />
          </div>
        }
        data-testid="operator-stores-table"
      >
        {filtered.map((s) => (
          <OperatorTableRow
            key={s.id}
            gridTemplate={GRID}
            onClick={() => nav(`/platform/companies/${cid}/stores/${s.id}`)}
            data-testid={`operator-store-row-${s.id}`}
          >
            <div className="min-w-0">
              <div className="font-display font-semibold truncate">
                {s.name}
              </div>
              <div className="font-mono text-[11px] text-muted-foreground truncate">
                {s.active === false ? "inactive" : "active"}
              </div>
            </div>
            <div className="font-mono text-xs uppercase">
              {s.code || "—"}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground truncate">
              {s.id}
            </div>
            <div className="text-right">
              <Button
                tier="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  nav(`/platform/companies/${cid}/stores/${s.id}`);
                }}
                data-testid={`operator-store-open-${s.id}`}
              >
                Open
              </Button>
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>

      {addOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3"
          onClick={() => setAddOpen(false)}
          data-testid="operator-stores-add-dialog"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md">
            <Card variant="raised" className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="font-display font-black text-xl">Add store</div>
                <button
                  onClick={() => setAddOpen(false)}
                  aria-label="Close"
                  className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"
                  data-testid="operator-stores-add-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="data-sku block mb-1">Name *</label>
                  <Input
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    placeholder="e.g. Downtown Flagship"
                    autoFocus
                    data-testid="operator-stores-add-name"
                  />
                </div>
                <div>
                  <label className="data-sku block mb-1">Code (optional)</label>
                  <Input
                    value={addCode}
                    onChange={(e) => setAddCode(e.target.value.toUpperCase())}
                    placeholder="e.g. DFL"
                    className="uppercase font-mono"
                    data-testid="operator-stores-add-code"
                  />
                </div>
                <Button
                  tier="primary"
                  className="w-full"
                  onClick={addStore}
                  disabled={adding || !addName.trim()}
                  data-testid="operator-stores-add-submit"
                >
                  {adding ? "Creating…" : "Create store"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
